import { useAtomValue } from "jotai";
import qs from "qs";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { OnboardingCTAType } from "~/components/tracking/google-analytics-4/types";
import { config } from "~/data";
import { accountAtom } from "~/lib/account";
import { useParsedCookie, useUserTypes } from "~/lib/hooks";
import { rosettaAtom } from "~/lib/rosetta";

import type { GaSlideImpressionType, OnboardQueryStringPayload, OnboardUserType } from "./types";

export const useNeedToShowOnboarding = () => {
  const parsedQueryString = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  }) as unknown as OnboardQueryStringPayload;
  return parsedQueryString.onboard === "true";
};

// NOTE: the order in which these user type appears is important and has been sorted by order of
//       importance
const getUserTypeFromList = (userTypeList: string[]): OnboardUserType => {
  if (userTypeList.includes("028")) return "028";
  if (userTypeList.includes("029")) return "029";
  if (userTypeList.includes("038")) return "038";
  if (userTypeList.includes("083")) return "083";
  if (userTypeList.includes("084")) return "084";
  if (userTypeList.includes("058")) return "058";
  if (userTypeList.includes("008")) return "008";
  if (userTypeList.includes("057")) return "057";
  if (userTypeList.includes("016")) return "016";
  if (userTypeList.includes("017")) return "017";
  if (userTypeList.includes("030")) return "030";
  if (userTypeList.includes("014")) return "014";

  return "default";
};

export const useOnboardingUserTypes = () => {
  const { value: forcedQaUserType } = useParsedCookie("qa_user_type", raw => raw, {
    initializeWithValue: true,
  });

  const parsedQueryString = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  }) as unknown as OnboardQueryStringPayload;
  const isFinishedMyNews = parsedQueryString.finishedMyNews === "true";

  const { user } = useAtomValue(accountAtom);
  const isAmp = user?.source.includes("amp");

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isScmpSubscriber = asyncRosettaState?.result?.isScmpSubscriber ?? false;

  const { userTypes } = useUserTypes();

  if (config.general.env !== "production" && forcedQaUserType)
    return forcedQaUserType as OnboardUserType;
  if (isScmpSubscriber) return "subscribers";
  if (isFinishedMyNews) return "myNews";
  if (isAmp) return "amp";

  if (
    parsedQueryString.firstTimeRegister === "true" &&
    parsedQueryString.signUpComplete === "true"
  ) {
    return "newsletter";
  }

  const userTypeIds = userTypes.map(element => element.id);
  if (userTypeIds.length === 0) return "default";

  const userType = getUserTypeFromList(userTypeIds);
  return userType;
};

interface HandleSlideTrackImpressionParameters {
  ctaType: OnboardingCTAType;
  currentSlide: number;
  impressionType: GaSlideImpressionType;
  isLastSlide: boolean;
  label?: string;
  userType: OnboardUserType;
}

export const useTrackImpression = () => {
  const asyncRosettaState = useAtomValue(rosettaAtom);

  const handleSlideTrackImpression = (parameters: HandleSlideTrackImpressionParameters) => {
    const {
      ctaType,
      currentSlide,
      impressionType,
      isLastSlide,
      label = window.location.href,
      userType,
    } = parameters;

    sendGATracking({
      action: `Onboarding/Pop-up/${userType}/${impressionType}/Impressions` as const,
      category: "Onboarding/Pop-up",
      label,
    });

    sendGA4Tracking({
      action: "imp",
      category: "onboarding",
      customized_parameters: {
        cta_type: ctaType,
        piano_exp_variant:
          asyncRosettaState?.result?.instance?.rulesEngine.getOutcome("tracking.abTestVariants"),
        step: currentSlide,
        user_type: userType,
      },
      subcategory: "",
    });

    if (isLastSlide) {
      sendGATracking({
        action: `Onboarding/Pop-up/${userType}/Complete` as const,
        category: "Onboarding/Pop-up",
        label,
      });
    }
  };

  return {
    handleSlideTrackImpression,
  };
};
